import PropTypes from 'prop-types'
import useSWR from 'swr'

import { isBlank, isPresent } from 'utils'

import useFilteredProducts from 'hooks/useFilteredProducts'
import useRouterQueryStateValue from 'hooks/useRouterQueryStateValue'

import CollectionHeader from 'components/CollectionHeader'
import Pagination from 'components/Pagination'
import ProductsFilters from 'components/ProductsFilters'
import ProductsGrid from 'components/ProductsGrid'
import ProductsSort from 'components/ProductsSort'
import Spacer from 'components/Spacer'
import Text from 'components/Text'

import { Filter, Header, Products, RootWithHeader, RootWithoutHeader, Sort, StatusBar } from './Collection.style'

const perPage = 9

const Collection = ({ handle, collection: collectionProp, facets: facetsProp, headerDisabled = false }) => {
  const swrEnabled = isPresent(handle) && isBlank(collection) && isBlank(facets)
  const { data, error, isValidating } = useSWR(swrEnabled ? `/collections/${handle}?v=2` : null, {
    revalidateOnMount: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  const { collection, facets } = data || {
    collection: collectionProp,
    facets: facetsProp,
  }

  const { state, products, totalPages } = useFilteredProducts({ products: collection.products, perPage })

  const [sortBy, setSortBy] = useRouterQueryStateValue('sortBy', {
    defaultValue: 'manual',
  })

  const Root = headerDisabled ? RootWithoutHeader : RootWithHeader

  if (swrEnabled && !data && !error) return null

  return (
    <Root>
      <>
        {!headerDisabled && (
          <Header>
            <CollectionHeader title={collection.title} description={collection.description} />
          </Header>
        )}
        <Sort>
          <ProductsSort value={sortBy} onChange={(value) => setSortBy(value)} />
        </Sort>
      </>
      <Filter>
        <ProductsFilters facets={facets} />
      </Filter>
      <Products>
        <StatusBar>
          <Text size="xs">
            {isValidating ? 'Loading…' : `Showing ${products.length} of ${collection.products.length} products`}
          </Text>
          {state.page !== 'all' && (
            <Text size="xs">
              Page {state.page || 1} of {totalPages}
            </Text>
          )}
        </StatusBar>
        <Spacer y={2} />
        <ProductsGrid products={products} baseUrl={collection.handle && `/collections/${collection.handle}/`} />
        {state.page !== 'all' && <Pagination page={state.page} totalPages={totalPages} shallow />}
      </Products>
    </Root>
  )
}

Collection.propTypes = {
  collection: PropTypes.object,
  facets: PropTypes.array,
  query: PropTypes.object,
}

Collection.defaultProps = {}

export default Collection
